import { getLogger } from 'src/utils/datadog-logger';
import type { EmbeddedOAuthSenderKind } from 'src/oauth/embedded-oauth-sender-kind';

const NONCE_STORAGE_KEY = 'dutchie:oauthFlowNonces';
const NONCE_TTL_MS = 5 * 60 * 1000;
const MAX_OUTSTANDING_NONCES = 5;

export type OAuthNonceOutcome = 'expired' | 'match' | 'mismatch' | 'missing';
export type OAuthProvider = 'apple' | 'google' | 'unknown';
export type OAuthNavType = 'back_forward' | 'navigate' | 'prerender' | 'reload' | 'unknown';

type Nonces = Record<string, number>;

const readNonces = (): Nonces => {
  const stored = window.localStorage.getItem(NONCE_STORAGE_KEY);
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored ?? '{}');
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(parsed).filter(
      ([nonce, timestamp]) =>
        /^[A-Za-z0-9_-]{16,64}$/.test(nonce) && typeof timestamp === 'number' && Number.isFinite(timestamp)
    )
  );
};

export const createOAuthFlowNonce = (): string | null => {
  try {
    const bytes = window.crypto.getRandomValues(new Uint8Array(24));
    const nonce = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const now = Date.now();
    const unexpired = Object.entries(readNonces())
      .filter(([, createdAt]) => createdAt <= now && now - createdAt <= NONCE_TTL_MS)
      .sort(([, first], [, second]) => first - second)
      .slice(-(MAX_OUTSTANDING_NONCES - 1));

    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(Object.fromEntries([...unexpired, [nonce, now]])));
    return nonce;
  } catch {
    return null;
  }
};

export const consumeOAuthFlowNonce = (nonce: string[] | string | null | undefined): OAuthNonceOutcome => {
  if (typeof nonce !== 'string' || !nonce) {
    return 'missing';
  }

  try {
    const nonces = readNonces();
    if (!Object.prototype.hasOwnProperty.call(nonces, nonce)) {
      return 'mismatch';
    }
    const createdAt = nonces[nonce];
    if (createdAt > Date.now() || Date.now() - createdAt > NONCE_TTL_MS) {
      return 'expired';
    }

    const remaining = Object.fromEntries(Object.entries(nonces).filter(([candidate]) => candidate !== nonce));
    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(remaining));
    return 'match';
  } catch {
    return 'mismatch';
  }
};

export const stripOAuthCallbackTokens = (): void => {
  const callbackUrl = new URL(window.location.href);
  callbackUrl.searchParams.delete('token');
  callbackUrl.searchParams.delete('oauth_nonce');
  window.history.replaceState(
    window.history.state,
    '',
    `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`
  );
};

type RedirectHostKind = 'custom' | 'dutchie' | 'none';
type CallbackQuery = {
  redirectUrl: string[] | string | undefined;
  embeddedPopup: string[] | string | undefined;
  hasToken: boolean;
  storedNonceCount: number;
};

// Navigation timing is best-effort context: a missing/odd API must never throw or log anything
// beyond the bounded union.
const readOAuthNavType = (): OAuthNavType => {
  try {
    if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
      return 'unknown';
    }
    // Typed as PerformanceEntry, whose declared surface lacks `type`; validated against the
    // union instead of trusting the runtime shape.
    const entry = performance.getEntriesByType('navigation')[0] as { type?: unknown } | undefined;
    const type = typeof entry?.type === 'string' ? entry.type : undefined;
    return type === 'navigate' || type === 'reload' || type === 'back_forward' || type === 'prerender'
      ? type
      : 'unknown';
  } catch {
    return 'unknown';
  }
};

export const countOAuthFlowNonces = (): number => {
  try {
    const now = Date.now();
    const count = Object.values(readNonces()).filter((createdAt) => createdAt <= now && now - createdAt <= NONCE_TTL_MS)
      .length;
    return Math.min(MAX_OUTSTANDING_NONCES, Math.max(0, count));
  } catch {
    return -1;
  }
};

let oauthCallbackReportCount = 0;

export const resetOAuthCallbackReportCountForTests = (): void => {
  oauthCallbackReportCount = 0;
};

const redirectHostKind = (value: string): RedirectHostKind => {
  try {
    const url = new URL(value, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'none';
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    return host === 'dutchie.com' || host.endsWith('.dutchie.com') ? 'dutchie' : 'custom';
  } catch {
    return 'none';
  }
};

const logger = getLogger('oauth-nonce');

export const reportOAuthNonceOutcome = (
  outcome: OAuthNonceOutcome,
  provider: OAuthProvider,
  route: string,
  { redirectUrl, embeddedPopup, hasToken, storedNonceCount }: CallbackQuery
): void => {
  const redirect = Array.isArray(redirectUrl) ? redirectUrl[redirectUrl.length - 1] : redirectUrl;
  const hasRedirectUrl = typeof redirect === 'string' && redirect.trim().length > 0;
  const popup = Array.isArray(embeddedPopup) ? embeddedPopup[embeddedPopup.length - 1] : embeddedPopup;
  oauthCallbackReportCount += 1;
  logger.info('OAuth callback nonce outcome', {
    outcome,
    oauth_provider: provider,
    route,
    has_redirect_url: hasRedirectUrl,
    redirect_host_kind: hasRedirectUrl ? redirectHostKind(redirect) : 'none',
    is_embedded_popup: popup === 'true',
    has_token: hasToken,
    nav_type: readOAuthNavType(),
    stored_nonce_count: storedNonceCount,
    is_repeat_in_document: oauthCallbackReportCount > 1,
  });
};

export const reportEmbeddedOAuthSender = (
  sameOrigin: boolean,
  isPopupSource: boolean,
  senderKind: EmbeddedOAuthSenderKind,
  trackedPopupCount: number,
  isConsumedPopup: boolean
): void => {
  logger.info('Embedded OAuth sender', {
    same_origin: sameOrigin,
    is_popup_source: isPopupSource,
    sender_kind: senderKind,
    tracked_popup_count: Math.min(5, Math.max(0, trackedPopupCount)),
    is_consumed_popup: isConsumedPopup,
  });
};
